/** SectionSlides — renders a lesson segment's slides inline as images.
 * Fed by the manifest's per-segment slides[] (resolved from slide_hint).
 *
 * WHY THE THUMBNAILS OPEN
 * -----------------------
 * Slides are 1734x975 native and render at 852x480 in the lesson column — 49%.
 * The decks' body type is authored at 22px for a 1280px canvas, so it lands at
 * roughly 8px here, against 14-16px for the surrounding lesson copy. Measured on
 * the live page; it is why the strip reads as "unreadable and too faint".
 *
 * Enlarging the thumbnails is the wrong lever: full-width buys about 1.4x, still
 * short of readable, and costs the at-a-glance index the strip exists to be.
 * Re-authoring the decks at larger type would break the locked theme in
 * RELEASE_STANDARD §2 and fix one course while leaving 18 mastery decks unreadable.
 *
 * So the strip stays a scannable index and the dialog is where you read. This
 * lives in the component every course shares, so all of them get it.
 */
import * as React from "react";
import { Box, Dialog, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import { useTranslation } from "react-i18next";
import type { SegmentSlide } from "../api/client";

const SLIDE_CAPTION_ID = "section-slide-caption";

export default function SectionSlides({ slides }: { slides: SegmentSlide[] }) {
  const { t } = useTranslation("pages-core");
  const [open, setOpen] = React.useState<SegmentSlide | null>(null);

  if (!slides || slides.length === 0) return null;

  return (
    <>
      <Box sx={{ my: 2, display: "grid", gap: 2 }}>
        {slides.map((s) => (
          <Box
            key={s.n}
            component="button"
            type="button"
            onClick={() => setOpen(s)}
            aria-label={t("SectionSlides.enlarge", { title: s.title })}
            sx={{
              p: 0,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              background: "none",
              display: "block",
              width: "100%",
              maxWidth: 880,
              cursor: "zoom-in",
              position: "relative",
              lineHeight: 0,
              transition: "border-color .15s",
              "&:hover, &:focus-visible": { borderColor: "primary.main" },
              "&:hover .zoom-affordance, &:focus-visible .zoom-affordance": { opacity: 1 },
            }}
          >
            <Box
              component="img"
              src={s.url}
              alt={s.title}
              loading="lazy"
              sx={{ width: "100%", height: "auto", display: "block", borderRadius: 2 }}
            />
            {/* Interactive things should look interactive — the thumbnail is a
                button now, and without this nothing says so until you hover. */}
            <Box
              className="zoom-affordance"
              sx={{
                position: "absolute",
                right: 8,
                bottom: 8,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                px: 1,
                py: 0.5,
                borderRadius: 1,
                bgcolor: "rgba(16,31,54,0.78)",
                color: "common.white",
                fontSize: 11,
                lineHeight: 1.4,
                letterSpacing: "0.04em",
                opacity: 0,
                transition: "opacity .15s",
                "@media (hover: none)": { opacity: 1 },
              }}
            >
              <ZoomInIcon sx={{ fontSize: 14 }} />
              {t("SectionSlides.zoomHint")}
            </Box>
          </Box>
        ))}
      </Box>

      <Dialog
        open={open !== null}
        onClose={() => setOpen(null)}
        maxWidth={false}
        fullWidth
        // aria-labelledby, NOT aria-label: MUI's Dialog destructures the former and
        // mints an id for it, but passes the latter through to the role="presentation"
        // Modal root — leaving the dialog unnamed with a dangling IDREF.
        aria-labelledby={open ? SLIDE_CAPTION_ID : undefined}
        PaperProps={{
          // NOT overflow:"visible". That overrode MUI's overflowY:auto while its
          // maxHeight:calc(100% - 64px) still applied, so on a short viewport ~122px
          // of the slide and the whole caption were cut off with no way to scroll —
          // defeating the one thing this dialog exists to do.
          sx: { bgcolor: "transparent", boxShadow: "none", m: 2, maxWidth: 1734 },
        }}
      >
        {open && (
          <Box sx={{ position: "relative" }}>
            <IconButton
              onClick={() => setOpen(null)}
              aria-label={t("SectionSlides.close")}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                zIndex: 1,
                bgcolor: "rgba(16,31,54,0.86)",
                color: "common.white",
                "&:hover": { bgcolor: "rgba(16,31,54,0.96)" },
              }}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <Box
              component="img"
              src={open.url}
              alt={open.title}
              sx={{ width: "100%", height: "auto", display: "block", borderRadius: 1 }}
            />
            <Typography
              id={SLIDE_CAPTION_ID}
              variant="caption"
              sx={{ display: "block", mt: 1, color: "common.white", textAlign: "center" }}
            >
              {open.title}
            </Typography>
          </Box>
        )}
      </Dialog>
    </>
  );
}
